import React, { useCallback, useEffect, useMemo, useState } from "react";
import { addDoc, collection } from "firebase/firestore";
import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  LinearProgress,
  CircularProgress as Spinner,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import Swal from "sweetalert2";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PaymentsIcon from "@mui/icons-material/Payments";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SavingsIcon from "@mui/icons-material/Savings";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PercentIcon from "@mui/icons-material/Percent";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import NavbarProject from "./NavbarProject";
import TableEdit from "./TableEdit";
import RowActionsMenu from "./RowActionsMenu";
import { dbFirebase } from "../Conections/Firebase";
import { fetchCollection } from "../Utils/firestore";
import { formatearNumero } from "../Utils/formatUtils";
import {
  formateoFechas,
  getCurrentDateTime,
  normalizarFecha,
} from "../Utils/dateUtils";
import { readFileAsBase64 } from "../Utils/fileUtils";
import { buildAmortizationSchedule, buildLoanView } from "../Utils/loanUtils";

const initialLoanForm = {
  nombre: "",
  entidad: "",
  monto: "",
  tasaAnual: "",
  plazoMeses: "",
  tipoPago: "fijo",
  fechaInicio: getCurrentDateTime(),
  descripcion: "",
};

const initialPaymentForm = {
  prestamoId: "",
  monto: "",
  fecha: getCurrentDateTime(),
  descripcion: "",
};

const formatDateCell = (value) => {
  if (!value) {
    return "-";
  }

  if (typeof value === "string" && value.includes("T")) {
    return formateoFechas(value);
  }

  return normalizarFecha(value) || value;
};

const PrestamosPropios = () => {
  const theme = useTheme();
  const [prestamos, setPrestamos] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [selectedLoanId, setSelectedLoanId] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("todos");
  const [detalleOpen, setDetalleOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openLoanDialog, setOpenLoanDialog] = useState(false);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [loanForm, setLoanForm] = useState(initialLoanForm);
  const [paymentForm, setPaymentForm] = useState(initialPaymentForm);
  const [loanFile, setLoanFile] = useState(null);
  const [pdfDialog, setPdfDialog] = useState({
    open: false,
    base64: null,
    title: "",
  });

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      const [prestamosData, pagosData] = await Promise.all([
        fetchCollection("prestamos_caja"),
        fetchCollection("prestamos_caja_pagos"),
      ]);

      setPrestamos(prestamosData);
      setPagos(pagosData);

      setSelectedLoanId((current) => current || prestamosData[0]?.id || "");
    } catch (error) {
      Swal.fire("Error", "No se pudieron cargar los préstamos: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const prestamosView = useMemo(
    () =>
      prestamos
        .map((prestamo) =>
          buildLoanView(
            prestamo,
            pagos.filter((item) => item.prestamoId === prestamo.id)
          )
        )
        .sort((a, b) => new Date(b.createdAt || b.fechaInicio || 0) - new Date(a.createdAt || a.fechaInicio || 0)),
    [prestamos, pagos]
  );

  const selectedLoan = useMemo(
    () => prestamosView.find((item) => item.id === selectedLoanId) || prestamosView[0] || null,
    [prestamosView, selectedLoanId]
  );

  const resumenGeneral = useMemo(
    () =>
      prestamosView.reduce(
        (acc, p) => {
          const liquidado = (p.resumen?.saldoActual || 0) <= 0;
          return {
            total: acc.total + 1,
            vigentes: acc.vigentes + (liquidado ? 0 : 1),
            liquidados: acc.liquidados + (liquidado ? 1 : 0),
            saldoPendiente: acc.saldoPendiente + (p.resumen?.saldoActual || 0),
            capitalPagado: acc.capitalPagado + (p.resumen?.totalCapitalPagado || 0),
          };
        },
        { total: 0, vigentes: 0, liquidados: 0, saldoPendiente: 0, capitalPagado: 0 }
      ),
    [prestamosView]
  );

  const prestamosFiltrados = useMemo(() => {
    if (filtroEstado === "vigentes") {
      return prestamosView.filter((p) => (p.resumen?.saldoActual || 0) > 0);
    }

    if (filtroEstado === "liquidados") {
      return prestamosView.filter((p) => (p.resumen?.saldoActual || 0) <= 0);
    }

    return prestamosView;
  }, [prestamosView, filtroEstado]);

  useEffect(() => {
    if (selectedLoan && selectedLoan.id !== selectedLoanId) {
      setSelectedLoanId(selectedLoan.id);
    }
  }, [selectedLoan, selectedLoanId]);

  const isDark = theme.palette.mode === "dark";

  const resumenCards = selectedLoan
    ? [
        {
          label: "Saldo actual",
          value: formatearNumero(selectedLoan.resumen.saldoActual || 0),
          icon: <SavingsIcon />,
          color: isDark ? "#fbbf24" : "#b45309",
        },
        {
          label: "Capital pagado",
          value: formatearNumero(selectedLoan.resumen.totalCapitalPagado || 0),
          icon: <TrendingUpIcon />,
          color: isDark ? "#4ade80" : "#15803d",
        },
        {
          label: "Interés pagado",
          value: formatearNumero(selectedLoan.resumen.totalInteresPagado || 0),
          icon: <PercentIcon />,
          color: isDark ? "#60a5fa" : "#1d4ed8",
        },
        {
          label: "Pago mensual estimado",
          value: formatearNumero(selectedLoan.pagoMensual || 0),
          icon: <CalendarMonthIcon />,
          color: isDark ? "#5eead4" : "#0f766e",
        },
      ]
    : [];

  const progresoCapital = selectedLoan
    ? Math.min(
        100,
        ((selectedLoan.resumen.totalCapitalPagado || 0) / (Number(selectedLoan.monto) || 1)) * 100
      )
    : 0;

  const handleCreateLoan = async () => {
    const monto = Number(loanForm.monto);
    const tasaAnual = Number(loanForm.tasaAnual);
    const plazoMeses = Number(loanForm.plazoMeses);

    if (!loanForm.nombre || !loanForm.entidad || !loanForm.fechaInicio) {
      Swal.fire("Faltan datos", "Completa el nombre, entidad y fecha de inicio.", "warning");
      return;
    }

    if (monto <= 0 || plazoMeses <= 0 || tasaAnual < 0) {
      Swal.fire(
        "Datos inválidos",
        "Monto, tasa anual y plazo deben tener valores válidos.",
        "warning"
      );
      return;
    }

    setLoading(true);

    try {
      const pdfBase64 = await readFileAsBase64(loanFile);
      const tablaAmortizacion = buildAmortizationSchedule({
        principal: monto,
        annualRate: tasaAnual,
        termMonths: plazoMeses,
        startDate: loanForm.fechaInicio,
        paymentType: loanForm.tipoPago,
      });

      await addDoc(collection(dbFirebase, "prestamos_caja"), {
        ...loanForm,
        monto,
        tasaAnual,
        plazoMeses,
        tablaAmortizacion,
        pdfBase64,
        pdfNombre: loanFile?.name || "",
        pdfTipo: loanFile?.type || "",
        createdAt: new Date().toISOString(),
      });

      setOpenLoanDialog(false);
      setLoanForm(initialLoanForm);
      setLoanFile(null);
      await loadData();

      Swal.fire("Registro exitoso", "El préstamo se guardó correctamente.", "success");
    } catch (error) {
      Swal.fire("Error", "No se pudo guardar el préstamo: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePayment = async () => {
    const monto = Number(paymentForm.monto);

    if (!paymentForm.prestamoId || !paymentForm.fecha || monto <= 0) {
      Swal.fire("Faltan datos", "Selecciona un préstamo, fecha y monto válido.", "warning");
      return;
    }

    setLoading(true);

    try {
      await addDoc(collection(dbFirebase, "prestamos_caja_pagos"), {
        ...paymentForm,
        monto,
        createdAt: new Date().toISOString(),
      });

      setOpenPaymentDialog(false);
      setPaymentForm({
        ...initialPaymentForm,
        prestamoId: paymentForm.prestamoId,
      });
      await loadData();

      Swal.fire("Pago registrado", "El pago se aplicó al préstamo seleccionado.", "success");
    } catch (error) {
      Swal.fire("Error", "No se pudo registrar el pago: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const loanColumns = [
    { key: "nombre", header: "Préstamo" },
    { key: "entidad", header: "Entidad" },
    {
      key: "monto",
      header: "Monto",
      render: (_, row) => formatearNumero(row.monto),
    },
    {
      key: "tasaAnual",
      header: "Tasa anual",
      render: (_, row) => `${Number(row.tasaAnual || 0).toFixed(2)}%`,
    },
    {
      key: "tipoPago",
      header: "Tipo",
      render: (_, row) => (row.tipoPago === "decreciente" ? "Decreciente" : "Fijo"),
    },
    {
      key: "plazoMeses",
      header: "Plazo",
      render: (_, row) => `${row.plazoMeses} meses`,
    },
    {
      key: "saldoActual",
      header: "Saldo actual",
      render: (_, row) => formatearNumero(row.resumen?.saldoActual || 0),
    },
    {
      key: "estado",
      header: "Estado",
      render: (_, row) => {
        const liquidado = (row.resumen?.saldoActual || 0) <= 0;
        return (
          <Chip
            size="small"
            color={liquidado ? "success" : "warning"}
            label={liquidado ? "Liquidado" : "Vigente"}
          />
        );
      },
    },
    {
      key: "acciones",
      header: "Acciones",
      render: (_, row) => (
        <RowActionsMenu
          primary={{
            label: "Ver detalle",
            variant: "outlined",
            onClick: () => {
              setSelectedLoanId(row.id);
              setDetalleOpen(true);
            },
          }}
          actions={[
            {
              label: "Registrar pago",
              icon: <PaymentsIcon fontSize="small" />,
              onClick: () => {
                setPaymentForm((current) => ({
                  ...current,
                  prestamoId: row.id,
                }));
                setOpenPaymentDialog(true);
              },
            },
            {
              label: "Ver PDF",
              icon: <PictureAsPdfIcon fontSize="small" />,
              disabled: !row.pdfBase64,
              onClick: () =>
                setPdfDialog({
                  open: true,
                  base64: row.pdfBase64,
                  title: row.pdfNombre || row.nombre,
                }),
            },
          ]}
        />
      ),
    },
  ];

  const paymentColumns = (paymentsApplied = []) => [
    {
      key: "fecha",
      header: "Fecha",
      render: (value) => formatDateCell(value),
    },
    {
      key: "monto",
      header: "Monto",
      render: (value) => formatearNumero(value),
    },
    {
      key: "capitalAplicado",
      header: "A capital",
      render: (value) => formatearNumero(value),
    },
    {
      key: "interesAplicado",
      header: "A interés",
      render: (value) => formatearNumero(value),
    },
    {
      key: "excedente",
      header: "Excedente",
      render: (value) => formatearNumero(value || 0),
    },
    {
      key: "descripcion",
      header: "Descripción",
    },
  ];

  return (
    <>
      <NavbarProject />
      <Grid container spacing={2} padding={2}>
        <Grid item xs={12}>
          <Typography variant="h5">Créditos de caja</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Registra préstamos propios, almacena el PDF de cotización en base64 y lleva el control
            de saldo, pagos y amortización.
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 2 }}>
            <Button variant="contained" onClick={() => setOpenLoanDialog(true)}>
              Agregar préstamo
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setPaymentForm((current) => ({
                  ...current,
                  prestamoId: selectedLoan?.id || current.prestamoId,
                }));
                setOpenPaymentDialog(true);
              }}
              disabled={!prestamosView.length}
            >
              Registrar pago
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={2}>
            {[
              {
                label: "Préstamos registrados",
                value: resumenGeneral.total,
                icon: <AccountBalanceWalletIcon />,
                color: theme.palette.mode === "dark" ? "#60a5fa" : "#1d4ed8",
              },
              {
                label: "Vigentes",
                value: resumenGeneral.vigentes,
                icon: <PlayCircleIcon />,
                color: theme.palette.mode === "dark" ? "#fbbf24" : "#b45309",
              },
              {
                label: "Liquidados",
                value: resumenGeneral.liquidados,
                icon: <CheckCircleIcon />,
                color: theme.palette.mode === "dark" ? "#4ade80" : "#15803d",
              },
              {
                label: "Saldo total pendiente",
                value: formatearNumero(resumenGeneral.saldoPendiente),
                icon: <SavingsIcon />,
                color: theme.palette.mode === "dark" ? "#fb923c" : "#c2410c",
              },
              {
                label: "Capital total pagado",
                value: formatearNumero(resumenGeneral.capitalPagado),
                icon: <PaymentsIcon />,
                color: theme.palette.mode === "dark" ? "#5eead4" : "#0f766e",
              },
            ].map((card) => (
              <Grid item xs={12} sm={6} md={2.4} key={card.label}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2.5,
                        display: "grid",
                        placeItems: "center",
                        mb: 1.5,
                        color: card.color,
                        bgcolor: alpha(card.color, theme.palette.mode === "dark" ? 0.22 : 0.12),
                      }}
                    >
                      {card.icon}
                    </Box>
                    <Typography variant="overline" color="text.secondary">
                      {card.label}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {card.value}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
              mb: 1.5,
            }}
          >
            <Typography variant="h6">Listado de préstamos</Typography>
            <ToggleButtonGroup
              size="small"
              value={filtroEstado}
              exclusive
              onChange={(e, value) => value && setFiltroEstado(value)}
              color="primary"
            >
              <ToggleButton value="todos">Todos ({resumenGeneral.total})</ToggleButton>
              <ToggleButton value="vigentes">Vigentes ({resumenGeneral.vigentes})</ToggleButton>
              <ToggleButton value="liquidados">
                Liquidados ({resumenGeneral.liquidados})
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <TableEdit columns={loanColumns} rows={prestamosFiltrados} />
        </Grid>

        {!prestamosView.length && !loading && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="body1">
                  Todavía no hay préstamos registrados en este módulo.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      <Dialog
        open={detalleOpen && Boolean(selectedLoan)}
        onClose={() => setDetalleOpen(false)}
        fullScreen
      >
        {selectedLoan && (
          <>
            <DialogTitle>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 2,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography variant="h6">{selectedLoan.nombre}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedLoan.entidad} | Inicio: {formatDateCell(selectedLoan.fechaInicio)} |{" "}
                    {selectedLoan.tipoPago === "decreciente" ? "Pago decreciente" : "Pago fijo"}
                  </Typography>
                </Box>
                <Chip
                  color={selectedLoan.resumen.saldoActual > 0 ? "warning" : "success"}
                  label={
                    selectedLoan.resumen.saldoActual > 0 ? "Con saldo pendiente" : "Liquidado"
                  }
                />
              </Box>
            </DialogTitle>
            <DialogContent dividers sx={{ bgcolor: theme.palette.background.default }}>
              <Container maxWidth="xl" disableGutters>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {selectedLoan.descripcion || "Sin descripción."}
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Capital pagado
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatearNumero(selectedLoan.resumen.totalCapitalPagado || 0)} de{" "}
                      {formatearNumero(selectedLoan.monto || 0)}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={progresoCapital}
                    sx={{
                      height: 10,
                      borderRadius: 999,
                      bgcolor: isDark ? alpha("#16a34a", 0.2) : "#e5efe8",
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 999,
                        background:
                          "linear-gradient(90deg, #15803d 0%, #16a34a 50%, #65a30d 100%)",
                      },
                    }}
                  />
                </Box>

                <Grid container spacing={2}>
                  {resumenCards.map((card) => (
                    <Grid item xs={12} sm={6} md={3} key={card.label}>
                      <Card sx={{ height: "100%" }}>
                        <CardContent>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 2.5,
                              display: "grid",
                              placeItems: "center",
                              mb: 1.5,
                              color: card.color,
                              bgcolor: alpha(card.color, isDark ? 0.22 : 0.12),
                            }}
                          >
                            {card.icon}
                          </Box>
                          <Typography variant="overline" color="text.secondary">
                            {card.label}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {card.value}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}

                  <Grid item xs={12} md={5}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Pagos registrados
                    </Typography>
                    <Card variant="outlined">
                      <CardContent>
                        <TableEdit
                          columns={paymentColumns(selectedLoan.paymentsApplied)}
                          rows={selectedLoan.paymentsApplied}
                        />
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={7}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Tabla de amortización
                    </Typography>
                    <TableContainer
                      component={Card}
                      variant="outlined"
                      sx={{ maxHeight: 520 }}
                    >
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>No.</TableCell>
                            <TableCell>Fecha</TableCell>
                            <TableCell align="right">Pago</TableCell>
                            <TableCell align="right">Capital</TableCell>
                            <TableCell align="right">Interés</TableCell>
                            <TableCell align="right">Pagado</TableCell>
                            <TableCell align="right">Pendiente</TableCell>
                            <TableCell align="right">Saldo vivo</TableCell>
                            <TableCell align="center">Estado</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedLoan.scheduleApplied.map((item, index) => (
                            <TableRow
                              key={item.numero}
                              sx={{
                                bgcolor:
                                  item.estado === "Parcial"
                                    ? alpha(theme.palette.warning.main, isDark ? 0.14 : 0.08)
                                    : index % 2 === 0
                                    ? "transparent"
                                    : theme.palette.action.hover,
                              }}
                            >
                              <TableCell>{item.numero}</TableCell>
                              <TableCell>{item.fechaProgramada}</TableCell>
                              <TableCell align="right">
                                {formatearNumero(item.pagoProgramado)}
                              </TableCell>
                              <TableCell align="right">
                                {formatearNumero(item.capitalProgramado)}
                              </TableCell>
                              <TableCell align="right">
                                {formatearNumero(item.interesProgramado)}
                              </TableCell>
                              <TableCell align="right">
                                {formatearNumero(item.totalPagado)}
                              </TableCell>
                              <TableCell align="right">
                                {formatearNumero(item.pendiente)}
                              </TableCell>
                              <TableCell align="right">
                                {formatearNumero(item.saldoVivo)}
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  size="small"
                                  label={item.estado}
                                  color={
                                    item.estado === "Pagado"
                                      ? "success"
                                      : item.estado === "Parcial"
                                      ? "warning"
                                      : "default"
                                  }
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </Grid>
              </Container>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetalleOpen(false)}>Cerrar</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Backdrop open={loading} sx={{ zIndex: 2000 }}>
        <Spinner color="inherit" />
      </Backdrop>

      <Dialog open={openLoanDialog} maxWidth="sm" fullWidth onClose={() => setOpenLoanDialog(false)}>
        <DialogTitle>Agregar préstamo de caja</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Nombre del préstamo"
            sx={{ mt: 2 }}
            value={loanForm.nombre}
            onChange={(e) => setLoanForm((current) => ({ ...current, nombre: e.target.value }))}
          />
          <TextField
            fullWidth
            label="Entidad o banco"
            sx={{ mt: 2 }}
            value={loanForm.entidad}
            onChange={(e) => setLoanForm((current) => ({ ...current, entidad: e.target.value }))}
          />
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Monto"
                type="number"
                value={loanForm.monto}
                onChange={(e) => setLoanForm((current) => ({ ...current, monto: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Tasa anual (%)"
                type="number"
                value={loanForm.tasaAnual}
                onChange={(e) =>
                  setLoanForm((current) => ({ ...current, tasaAnual: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Plazo (meses)"
                type="number"
                value={loanForm.plazoMeses}
                onChange={(e) =>
                  setLoanForm((current) => ({ ...current, plazoMeses: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Tipo de pago</InputLabel>
                <Select
                  value={loanForm.tipoPago}
                  label="Tipo de pago"
                  onChange={(e) =>
                    setLoanForm((current) => ({ ...current, tipoPago: e.target.value }))
                  }
                >
                  <MenuItem value="fijo">Fijo</MenuItem>
                  <MenuItem value="decreciente">Decreciente</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <TextField
            fullWidth
            label="Fecha de inicio"
            type="datetime-local"
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 2 }}
            value={loanForm.fechaInicio}
            onChange={(e) => setLoanForm((current) => ({ ...current, fechaInicio: e.target.value }))}
          />
          <TextField
            fullWidth
            label="Descripción"
            multiline
            rows={3}
            sx={{ mt: 2 }}
            value={loanForm.descripcion}
            onChange={(e) => setLoanForm((current) => ({ ...current, descripcion: e.target.value }))}
          />
          <TextField
            fullWidth
            type="file"
            label="Cotización PDF"
            InputLabelProps={{ shrink: true }}
            inputProps={{ accept: "application/pdf" }}
            sx={{ mt: 2 }}
            onChange={(e) => setLoanFile(e.target.files?.[0] || null)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenLoanDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreateLoan}>
            Guardar préstamo
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openPaymentDialog}
        maxWidth="sm"
        fullWidth
        onClose={() => setOpenPaymentDialog(false)}
      >
        <DialogTitle>Registrar pago</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Préstamo"
            SelectProps={{ native: true }}
            sx={{ mt: 2 }}
            value={paymentForm.prestamoId}
            onChange={(e) =>
              setPaymentForm((current) => ({ ...current, prestamoId: e.target.value }))
            }
          >
            <option value="">Selecciona un préstamo</option>
            {prestamosView.map((prestamo) => (
              <option key={prestamo.id} value={prestamo.id}>
                {prestamo.nombre} - {prestamo.entidad}
              </option>
            ))}
          </TextField>
          <TextField
            fullWidth
            label="Monto pagado"
            type="number"
            sx={{ mt: 2 }}
            value={paymentForm.monto}
            onChange={(e) => setPaymentForm((current) => ({ ...current, monto: e.target.value }))}
          />
          <TextField
            fullWidth
            label="Fecha del pago"
            type="datetime-local"
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 2 }}
            value={paymentForm.fecha}
            onChange={(e) => setPaymentForm((current) => ({ ...current, fecha: e.target.value }))}
          />
          <TextField
            fullWidth
            label="Descripción"
            multiline
            rows={3}
            sx={{ mt: 2 }}
            value={paymentForm.descripcion}
            onChange={(e) =>
              setPaymentForm((current) => ({ ...current, descripcion: e.target.value }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPaymentDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreatePayment}>
            Registrar pago
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={pdfDialog.open}
        onClose={() => setPdfDialog({ open: false, base64: null, title: "" })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>{pdfDialog.title || "Cotización"}</DialogTitle>
        <DialogContent>
          {pdfDialog.base64 ? (
            <iframe
              title="Cotización PDF"
              src={pdfDialog.base64}
              style={{ width: "100%", height: "70vh", border: 0 }}
            />
          ) : (
            <Typography>No hay archivo disponible.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPdfDialog({ open: false, base64: null, title: "" })}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PrestamosPropios;
